import { For, Match, ParentProps, Setter, Show, Signal, Switch, createContext, createEffect, createMemo, createSignal, on, onMount } from 'solid-js'
import { createProxySignal, date, requireContext } from 'common'
import { recurPattern as api, Recur } from 'st-app/api'
import { RecurPatternContext } from './RecurPatternContext'
import styles from './st-recur-pattern.module.css'
import Select from '../st-select/st-select'
import GroupBox from '../st-group-box/st-group-box'
import Input from '../st-input/st-input'
import TimePicker from '../st-time-picker/st-time-picker'
import DatePicker from '../st-date-picker/st-date-picker'
import Checkbox from '../st-checkbox/st-checkbox'
import RadioGroup from '../st-radio-group/st-radio-group'
import { createStore } from 'solid-js/store'

const RecurPatternContextJsx = createContext<RecurPatternContext>();
export const requireRecurPatternContext = () => {
    return requireContext(RecurPatternContextJsx);
}

export class RecurPatternProps {
    value?: string
    setValue?: Setter<string>
}

export default function RecurPattern(props: ParentProps<RecurPatternProps>) {
    const context = new RecurPatternContext();

    return (
        <RecurPatternContextJsx.Provider value={context}>
            <RecurPatternInternal {...props} />
        </RecurPatternContextJsx.Provider>
    )
}

interface LookupValue {
    order: number
    code: string
    description: string
}

const CODE_ONCE = '0';
const CODE_DAILY = '1';
const CODE_WEEKLY = '2';
const CODE_MONTHLY = '3';

const SCHEDULE_TASK_OPTIONS: readonly LookupValue[] = [
    { order: 1, code: '0', description: 'Once' },
    { order: 2, code: '1', description: 'Daily' },
    { order: 3, code: '2', description: 'Weekly' },
    { order: 4, code: '3', description: 'Monthly' },
]

const MONTH_DAY_TYPE_OPTIONS: readonly LookupValue[] = [
    { order: 1, code: '1', description: 'First' },
    { order: 2, code: '2', description: 'Second' },
    { order: 3, code: '3', description: 'Third' },
    { order: 4, code: '4', description: 'Fourth' },
    { order: 5, code: '5', description: 'Last' },
]

const MONTH_DAY_OF_WEEK_OPTIONS: readonly LookupValue[] = [
    { order: 1, code: '0', description: 'Sunday' },
    { order: 2, code: '1', description: 'Monday' },
    { order: 3, code: '2', description: 'Tuesday' },
    { order: 4, code: '3', description: 'Wednesday' },
    { order: 5, code: '4', description: 'Thursday' },
    { order: 6, code: '5', description: 'Friday' },
    { order: 7, code: '6', description: 'Saturday' },
]


function RecurPatternInternal(props: ParentProps<RecurPatternProps>) {
    const [recur, setRecur] = createStore<Recur>(props.value ? JSON.parse(props.value) : new Recur());

    createEffect(() => {
        if (props.value) {
            const value = JSON.parse(props.value);
            setRecur(value);
        }
    })


    const [recurType, setRecurType] = createProxySignal(() => recur.type.toString(), (v) => setRecur('type', parseInt(v)));
    const [startTime, setStartTime] = createProxySignal<Date | undefined>(() => date.fromJson(recur.startTime), (v) => setRecur('startTime', date.toJson(v)));
    const [startDate, setStartDate] = createProxySignal<Date | undefined>(() => date.fromJson(recur.startDate), (v) => setRecur('startDate', date.toJson(v)));
    const [stopDate, setStopDate] = createProxySignal<Date | undefined>(() => date.fromJson(recur.stopDate), (v) => setRecur('stopDate', date.toJson(v)));
    const [onlyValidSchoolDays, setOnlyValidSchoolDays] = createProxySignal(() => recur.onlyScheduleForValidDays, (v) => setRecur('onlyScheduleForValidDays', v));

    const [dayCount, setDayCount] = createProxySignal(() => recur.dayCount.toString(), (v) => setRecur('dayCount', parseInt(v)));
    const [weekCount, setWeekCount] = createProxySignal(() => recur.weekCount.toString(), (v) => setRecur('weekCount', parseInt(v)));

    const [monday, setMonday] = createProxySignal(() => recur.monday, (v) => setRecur('monday', v));
    const [tuesday, setTuesday] = createProxySignal(() => recur.tuesday, (v) => setRecur('tuesday', v));
    const [wednesday, setWednesday] = createProxySignal(() => recur.wednesday, (v) => setRecur('wednesday', v));
    const [thursday, setThursday] = createProxySignal(() => recur.thursday, (v) => setRecur('thursday', v));
    const [friday, setFriday] = createProxySignal(() => recur.friday, (v) => setRecur('friday', v));
    const [saturday, setSaturday] = createProxySignal(() => recur.saturday, (v) => setRecur('saturday', v));
    const [sunday, setSunday] = createProxySignal(() => recur.sunday, (v) => setRecur('sunday', v));

    const [january, setJanuary] = createProxySignal(() => recur.january, (v) => setRecur('january', v));
    const [february, setFebruary] = createProxySignal(() => recur.february, (v) => setRecur('february', v));
    const [march, setMarch] = createProxySignal(() => recur.march, (v) => setRecur('march', v));
    const [april, setApril] = createProxySignal(() => recur.april, (v) => setRecur('april', v));
    const [may, setMay] = createProxySignal(() => recur.may, (v) => setRecur('may', v));
    const [june, setJune] = createProxySignal(() => recur.june, (v) => setRecur('june', v));
    const [july, setJuly] = createProxySignal(() => recur.july, (v) => setRecur('july', v));
    const [august, setAugust] = createProxySignal(() => recur.august, (v) => setRecur('august', v));
    const [september, setSeptember] = createProxySignal(() => recur.september, (v) => setRecur('september', v));
    const [october, setOctober] = createProxySignal(() => recur.october, (v) => setRecur('october', v));
    const [november, setNovember] = createProxySignal(() => recur.november, (v) => setRecur('november', v));
    const [december, setDecember] = createProxySignal(() => recur.december, (v) => setRecur('december', v));

    const [monthType, setMonthType] = createProxySignal(() => recur.monthType.toString(), (v) => setRecur('monthType', parseInt(v)));
    const [monthDayType, setMonthDayType] = createProxySignal(() => recur.monthDayType.toString(), (v) => setRecur('monthDayType', parseInt(v)));
    const [monthDayOfWeek, setMonthDayOfWeek] = createProxySignal(() => recur.monthDayofWeek.toString(), (v) => setRecur('monthDayofWeek', parseInt(v)));
    const [monthDayofMonth, setMonthDayofMonth] = createProxySignal(() => (recur.monthDayofMonth || 1).toString(), (v) => setRecur('monthDayofMonth', parseInt(v)));

    const [nextRunDate, setNextRunDate] = createSignal<string>('');

    createEffect(on(() => [JSON.stringify(recur)], () => {
        props.setValue?.(JSON.stringify(recur));
    }))

    const onClick = async () => {
        const result = await api.GetNextRecurDate(recur);
        setNextRunDate(result);
    }

    return (
        <div class={styles['st-recur-pattern']}>
            <GroupBox label="Schedule Job">
                <div class='flexbox horizontal spaced'>
                    <Field label="Schedule Task">
                        <Select value={[recurType, setRecurType]}>
                            <For each={SCHEDULE_TASK_OPTIONS}>{(option) => (
                                <Select.Item value={option.code}>{option.description}</Select.Item>
                            )}</For>
                        </Select>
                    </Field>

                    <Show when={recurType() !== CODE_ONCE}>
                        <Field label="Start Time">
                            <TimePicker value={[startTime, setStartTime]} />
                        </Field>

                        <Field label="Start Date">
                            <DatePicker value={[startDate, setStartDate]} />
                        </Field>

                        <Field label="Stop Date">
                            <DatePicker value={[stopDate, setStopDate]} />
                        </Field>

                        <Checkbox label='Only Schedule for Valid School Days' value={[onlyValidSchoolDays, setOnlyValidSchoolDays]} />
                    </Show>
                </div>

                <div>
                    This report print immediately and then per schedule.
                </div>
            </GroupBox>

            <Switch>
                <Match when={recurType() === CODE_ONCE}><></></Match>
                <Match when={recurType() === CODE_DAILY}>
                    <GroupBox label="Schedule Daily" class="flexbox horizontal middle spaced">
                        Every&nbsp;
                        <Input type="number" value={[dayCount, setDayCount]} />
                        &nbsp;Day(s)
                    </GroupBox>
                </Match>

                <Match when={recurType() === CODE_WEEKLY}>
                    <GroupBox label="Schedule Weekly" class="flexbox horizontal middle spaced">
                        <div class='flexbox horizontal middle spaced' style='align-self: start; white-space: nowrap;'>
                            Every&nbsp;
                            <Input type="number" value={[weekCount, setWeekCount]} />
                            &nbsp;Week(s) on:
                        </div>

                        <div class='spaced' style='display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: repeat(5, 1fr); grid-auto-flow: column;'>
                            <Checkbox label='Monday' value={[monday, setMonday]} />
                            <Checkbox label='Tuesday' value={[tuesday, setTuesday]} />
                            <Checkbox label='Wednesday' value={[wednesday, setWednesday]} />
                            <Checkbox label='Thursday' value={[thursday, setThursday]} />
                            <Checkbox label='Friday' value={[friday, setFriday]} />
                            <Checkbox label='Saturday' value={[saturday, setSaturday]} />
                            <Checkbox label='Sunday' value={[sunday, setSunday]} />
                        </div>
                    </GroupBox>
                </Match>
                <Match when={recurType() === CODE_MONTHLY}>
                    <GroupBox label='Schedule Task Monthly' class='flexbox horizontal spaced'>
                        <RadioGroup value={[monthType, setMonthType]} vertical>
                            <RadioGroup.Option value={'0'}>
                                <div class='flexbox horizontal middle spaced' style='white-space: nowrap;'>
                                    Day
                                    <Select value={[monthDayofMonth, setMonthDayofMonth]}>
                                        <For each={Array.from({ length: 31 }, (_, i) => i + 1)}>{(day) => (
                                            <Select.Item value={day.toString()}>{day.toString()}</Select.Item>
                                        )}</For>
                                    </Select>
                                    of the month
                                </div>
                            </RadioGroup.Option>

                            <RadioGroup.Option value={'1'}>
                                <div class='flexbox horizontal middle spaced' style='white-space: nowrap;'>
                                    The
                                    <Select value={[monthDayType, setMonthDayType]}>
                                        <For each={MONTH_DAY_TYPE_OPTIONS}>{(option) => (
                                            <Select.Item value={option.code}>{option.description}</Select.Item>
                                        )}</For>
                                    </Select>
                                    <Select value={[monthDayOfWeek, setMonthDayOfWeek]}>
                                        <For each={MONTH_DAY_OF_WEEK_OPTIONS}>{(option) => (
                                            <Select.Item value={option.code}>{option.description}</Select.Item>
                                        )}</For>
                                    </Select>
                                    of the month
                                </div>
                            </RadioGroup.Option>
                        </RadioGroup>

                        <div class='spaced' style='display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: repeat(6, 1fr); grid-auto-flow: column;'>
                            <Checkbox label='January' value={[january, setJanuary]} />
                            <Checkbox label='February' value={[february, setFebruary]} />
                            <Checkbox label='March' value={[march, setMarch]} />
                            <Checkbox label='April' value={[april, setApril]} />
                            <Checkbox label='May' value={[may, setMay]} />
                            <Checkbox label='June' value={[june, setJune]} />
                            <Checkbox label='July' value={[july, setJuly]} />
                            <Checkbox label='August' value={[august, setAugust]} />
                            <Checkbox label='September' value={[september, setSeptember]} />
                            <Checkbox label='October' value={[october, setOctober]} />
                            <Checkbox label='November' value={[november, setNovember]} />
                            <Checkbox label='December' value={[december, setDecember]} />
                        </div>
                    </GroupBox>
                </Match>
            </Switch>
        </div>
    );
}



interface FieldProps {
    label: string 
}

function Field(props: ParentProps<FieldProps>) {
    return(
        <div class={styles['field']}>
            <label>{props.label}</label>
            {props.children}
        </div>
    )
}

RecurPattern.defaultProps = new RecurPatternProps();
